import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kUserFilesApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'
import { SignedUploadUrlResponse, SignedUploadUrlResponseSchema } from '../schemas'

type GetSignedUploadUrlArgs = {
  fileId: string
  contentType: string
}

export async function getSignedUploadUrl({ fileId, contentType }: GetSignedUploadUrlArgs): Promise<SignedUploadUrlResponse> {
  const sentryTransactionId = nanoid()
  const auth = getAuth()
  const token = await auth.currentUser?.getIdToken()

  if (!token) {
    throw new Error('User not authenticated')
  }

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesApiBasePath}/${encodeURIComponent(fileId)}/upload-url`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Transaction-Id': sentryTransactionId,
    },
    body: JSON.stringify({ content_type: contentType }),
  })

  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    const errorData = parseError ? {} : data
    const detail = formatUserFilesErrorDetail(errorData)
    throw new ApiError(response.status, detail || `Failed to create upload URL: ${response.status}`)
  }

  if (parseError) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentryTransactionId })
      Sentry.captureException(new Error('Signed upload URL response JSON parse failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          fileId,
          contentType,
          parseError: formatUnknownError(parseError),
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid signed upload URL response JSON')
  }

  const parsed = SignedUploadUrlResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentryTransactionId })
      Sentry.captureException(new Error('Signed upload URL response schema validation failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          fileId,
          contentType,
          responseKeys: getTopLevelKeys(data),
          issues: parsed.error.issues,
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid signed upload URL response shape', parsed.error.issues)
  }

  return parsed.data
}
